import TicketsResource from 'flow/api/omnitickets';
import TicketsSettingsResource from 'flow/api/omniticketsetting';
import Actions from 'flow/actions';
import { i18next } from 'flow/configure-locale';

import {
    selectOpenTicketsAssignedToLoggedInUserRequestPageNumber,
    selectOpenTicketsUnassignedRequestPageNumber,
    selectTicketsAllStatesRequestPageNumber,
    selectClosedTicketsRequestPageNumber,
    selectRelatedTicketsRequestPageNumber,
    selectTicketsListIsMounted,
    selectTicketsListTabSelected,
    selectTicket,
    selectTicketsSettingsForQueue,
    selectTicketEventsIsMounted,
    selectTicketsTabIsEnforced,
    selectOriginDTOSubtype,
    selectTicketsSortForApi,
} from 'flow/selectors/TicketsSelectors';

import {
    onOwnUserRemovedFromTicket,
    onTicketWriteRefused,
} from 'flow/actions/ticket-actions';
import { getOwnExtensionKey } from 'flow/selectors/selectors';
import {
    networkErrorDispatcher,
    spawnTicketNotification,
} from 'flow/actions/app-actions';
import { getExtensionContactFirstNameLastName } from 'flow/selectors/extension-selectors';
import { getExtension } from 'flow/selectors/ExtensionSelectors';

import { getContactName } from 'flow/utils/contact-utils';
import { addTicketLogEntry } from 'flow/utils/browser-utils';
import { CALL_RECORD_INCLUDES_TICKET_EVENT } from 'flow/utils/call-record-fetch-includes';
import { eventIsCallRecordEvent } from 'flow/types/TicketEventData';

import {
    TICKETS_LIST_TABS_KEYS,
    getTicketIdUiFriendlyFromTicketKey,
    ticketVisibilityFromWire,
} from 'flow/utils/ticket-utils';

import {
    getStringWithFirstLetterFromSecondWord,
    isExtensionKey,
} from 'flow/utils';
import {
    selectShouldSeeTicketsTab,
    selectTagExistsInAvailableTags,
    selectTagFromAllTagsWithLabel,
    selectTicketIsFetched,
    selectTicketViewsMountedData,
} from 'flow/selectors/TicketsSelectors';
import {
    selectOwnExtensionKey,
    selectUserCountryCode,
} from 'flow/selectors/ExtensionSelectors';
import {
    selectAppIsVisibleAndFocused,
    selectPbxPageIsMounted,
} from 'flow/selectors/app-selectors';
import icon_whatsapp from 'images/omnichannel/whatsapp_black.png';
import icon_email from 'images/omnichannel/omnichannel_email.svg';
import { prettyFormatE164Number } from 'flow/utils/call-utils';
import { isTypeQueueKey } from 'flow/utils/pbx-utils-TW';
import { selectQueueMembers } from 'flow/selectors/queue-selectors';
import { selectMessageEventLacksStylings } from 'flow/selectors/TicketEventSelectors';
import { getPlaceholderEventsFromLocalStorage } from 'flow/components/tickets/Input/InputActions';

const TICKETS_PAGE_SIZE = 25;
const ONE_SECOND_MILLISECONDS = 1000;

// Shared request plumbing for the tab lists. Each tab keeps its own fetch
// method (below) that supplies the appropriate assignee/state filter; they all
// hit the generic tickets endpoint with the globally selected sort applied.
const fetchTicketsListPage =
    ({ pageNumber, nextPageNumber, assignee, state: ticketState, setData }) =>
    (dispatch, getState) =>
        new Promise((resolve, reject) => {
            const { sortField, sortDirection } =
                selectTicketsSortForApi(getState());
            TicketsResource.getTickets(
                {
                    onResult(json) {
                        const apiHasMoreData =
                            json.length === TICKETS_PAGE_SIZE;
                        dispatch(setData(json, nextPageNumber, apiHasMoreData));
                        resolve(json);
                    },
                    onClientError: (/*error*/) => {
                        reject();
                    },
                    onError: (/*error*/) => {
                        networkErrorDispatcher(dispatch);
                        reject();
                    },
                },
                TICKETS_PAGE_SIZE,
                pageNumber,
                undefined, // searchParam
                assignee,
                ticketState,
                undefined, // since
                undefined, // countOnly
                undefined, // marked
                undefined, // sessionIds
                undefined, // tagKeysOr
                sortField,
                sortDirection,
            );
        });

export const fetchTicket = (ticketKey) => (dispatch, getState) => {
    return new Promise((resolve, reject) => {
        TicketsResource.getTicket(
            {
                onResult(json) {
                    const ownExtensionKey = getOwnExtensionKey(getState());
                    dispatch(setTicketData(json, ownExtensionKey));
                    resolve(json);
                },
                onClientError: (error) => {
                    reject(error);
                },
                onError: (error) => {
                    networkErrorDispatcher(dispatch);
                    reject(error);
                },
            },
            ticketKey,
        );
    });
};

export const createTicketForOngoingQueueCall =
    (locutusSessionId) => (dispatch, getState) => {
        if (!locutusSessionId)
            return Promise.reject({ message: 'No locutusSessionId provided' });
        const callRecordKey = `call-${locutusSessionId}`;
        const ownExtensionKey = selectOwnExtensionKey(getState());
        return new Promise((resolve, reject) => {
            dispatch(
                createTicketForQueueCall(
                    callRecordKey,
                    [ownExtensionKey],
                    ownExtensionKey,
                    true,
                ),
            )
                .then((data) => {
                    resolve(data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    };

export const createTicketForQueueCall =
    (callRecordEntityKey, members, assignee, lookForQueueCallParent = false) =>
    (dispatch, getState) => {
        const payload = {
            callRecordEntityKey,
            members,
            assignee,
            lookForQueueCallParent,
        };
        return new Promise((resolve, reject) => {
            TicketsResource.createNewTicket(
                {
                    onResult(data) {
                        const queueKey = data.origin?.key;
                        const ticketKey = data.key;
                        dispatch(
                            setTicketKeyForQueueCallRecord(
                                queueKey,
                                callRecordEntityKey,
                                ticketKey,
                            ),
                        );

                        const ownExtensionKey =
                            selectOwnExtensionKey(getState());
                        dispatch(setTicketData(data, ownExtensionKey));
                        resolve(data);
                    },
                    onClientError: (response) => {
                        reject(response);
                    },
                    onError: (response) => {
                        networkErrorDispatcher(dispatch);
                        reject(response);
                    },
                },
                payload,
            );
        });
    };

// Method below, sendMessage, to be deleted in favor of method above,
// sendInternalNote, once v1 components are to be removed
export const sendMessage =
    (ticketKey, message, mentions) => (dispatch, getState) => {
        const state = getState();
        const internalNoteDTOForApi = {
            DTOSubtype: 'OmniInternalNoteTicketEvent',
            message: message,
            stylings: mentions,
            sender: getOwnExtensionKey(state),
            read: true,
        };
        //TODO: #team-omni this needs some consideration what if we fail??
        TicketsResource.addInternalNote(
            {
                onResult(json) {
                    dispatch(addTicketEvent(json, ticketKey));
                },
                onError: (/*error*/) => {
                    networkErrorDispatcher(dispatch);
                },
            },
            ticketKey,
            internalNoteDTOForApi,
        );
    };

export const sendMessageAsSMSv1Method =
    (ticketKey, message) => (dispatch, getState) => {
        const state = getState();
        const smsNoteDTOForApi = {
            DTOSubtype: 'OmniSmsNoteTicketEvent',
            message: message,
            sender: getOwnExtensionKey(state),
            read: true,
        };

        //TODO: #team-omni this needs some consideration what if we fail??
        TicketsResource.sendSMS(
            {
                onResult(json) {
                    dispatch(addTicketEvent(json, ticketKey));
                },
                onError: (/*error*/) => {
                    networkErrorDispatcher(dispatch);
                },
            },
            ticketKey,
            smsNoteDTOForApi,
        );
    };

export const fetchTicketEvents = (ticketKey) => (dispatch, getState) => {
    const messageTypeToRequest =
        selectOriginDTOSubtype(getState(), ticketKey) === 'email' ? 'rich' : '';
    return new Promise((resolve, reject) => {
        TicketsResource.getTicketEvents(
            {
                onResult(data) {
                    const placeholderEvents =
                        getPlaceholderEventsFromLocalStorage(ticketKey);
                    const mergedData = data.concat(placeholderEvents);
                    dispatch(setTicketEvents(mergedData, ticketKey));
                    resolve(data);
                },
                onClientError: (error) => {
                    reject(error);
                },
                onError: (error) => {
                    reject(error);
                },
            },
            ticketKey,
            messageTypeToRequest,
            3,
        );
    });
};

export const fetchTicketMessage =
    (ticketKey, conversationMessageKey, useAttachmentDataOnly) =>
    (dispatch, getState) => {
        const messageTypeToRequest =
            selectOriginDTOSubtype(getState(), ticketKey) === 'email'
                ? 'rich'
                : '';
        return new Promise((resolve, reject) => {
            TicketsResource.getConversationMessage(
                {
                    onResult(response) {
                        if (useAttachmentDataOnly) {
                            dispatch(
                                setTicketEventAttachments(
                                    ticketKey,
                                    conversationMessageKey,
                                    response?.attachments,
                                ),
                            );
                        } else {
                            dispatch(addTicketEvent(response, ticketKey));
                        }
                        resolve(response);
                    },
                    onClientError: (error) => {
                        reject(error);
                    },
                    onError: (error) => {
                        reject(error);
                    },
                },
                ticketKey,
                conversationMessageKey,
                messageTypeToRequest,
            );
        });
    };

const setTicketEventAttachments = (ticketKey, eventKey, data) => (dispatch) => {
    dispatch({
        type: Actions.SET_TICKET_EVENT_ATTACHMENTS,
        ticketKey,
        eventKey,
        data,
    });
};

export const fetchTicketsSettingsForQueue = (queueEntityKey) => (dispatch) => {
    return new Promise((resolve, reject) => {
        TicketsSettingsResource.fetchTicketSettings(
            {
                onResult(json) {
                    dispatch(
                        setTicketsSettingsInStoreForQueue(queueEntityKey, json),
                    );
                    resolve();
                },
                onClientError: (/*error*/) => {
                    reject();
                },
                onError: (/*error*/) => {
                    networkErrorDispatcher(dispatch);
                    reject();
                },
            },
            queueEntityKey,
        );
    });
};

const createTicketsSettingsForQueue =
    (queueEntityKey, ticketSettingsDTO) => (dispatch) => {
        return new Promise((resolve, reject) => {
            TicketsSettingsResource.createTicketSettings(
                {
                    onResult(json) {
                        dispatch(
                            setTicketsSettingsInStoreForQueue(
                                queueEntityKey,
                                json,
                            ),
                        );
                        resolve();
                    },
                    onClientError: (/*error*/) => {
                        reject();
                    },
                    onError: (/*error*/) => {
                        networkErrorDispatcher(dispatch);
                        reject();
                    },
                },
                queueEntityKey,
                ticketSettingsDTO,
            );
        });
    };

const updateTicketsSettingsForQueue =
    (ticketSettingsKey, queueEntityKey, ticketSettingsDTO) => (dispatch) => {
        return new Promise((resolve, reject) => {
            TicketsSettingsResource.updateTicketSettings(
                {
                    onResult(json) {
                        dispatch(
                            setTicketsSettingsInStoreForQueue(
                                queueEntityKey,
                                json,
                            ),
                        );
                        resolve();
                    },
                    onClientError: (/*error*/) => {
                        reject();
                    },
                    onError: (/*error*/) => {
                        networkErrorDispatcher(dispatch);
                        reject();
                    },
                },
                ticketSettingsKey,
                ticketSettingsDTO,
            );
        });
    };

export const fetchDefaultTicketVisibilityForQueue =
    (queueEntityKey) => (dispatch) => {
        return new Promise((resolve, reject) => {
            TicketsResource.getDefaultTicketVisibility(
                {
                    onResult(json) {
                        dispatch(
                            setDefaultTicketVisibilityInStoreForQueue(
                                queueEntityKey,
                                json,
                            ),
                        );
                        resolve();
                    },
                    onClientError: (/*error*/) => {
                        reject();
                    },
                    onError: (/*error*/) => {
                        networkErrorDispatcher(dispatch);
                        reject();
                    },
                },
                queueEntityKey,
            );
        });
    };

export const setDefaultTicketVisibilityForQueue =
    (queueEntityKey, visibility) => (dispatch) => {
        return new Promise((resolve, reject) => {
            TicketsResource.setDefaultTicketVisibility(
                {
                    onResult(json) {
                        dispatch(
                            setDefaultTicketVisibilityInStoreForQueue(
                                queueEntityKey,
                                json,
                            ),
                        );
                        resolve();
                    },
                    onClientError: (/*error*/) => {
                        reject();
                    },
                    onError: (/*error*/) => {
                        networkErrorDispatcher(dispatch);
                        reject();
                    },
                },
                queueEntityKey,
                { visibility },
            );
        });
    };

export const setTicketsSettingsForQueue =
    (queueEntityKey, settingPathName, value) => (dispatch, getState) => {
        const ticketsSettingsDTO = selectTicketsSettingsForQueue(
            getState(),
            queueEntityKey,
        ).set(settingPathName, value);

        const ticketSettingsKey = ticketsSettingsDTO.get('key');
        const ticketSettingsDTOApiFriendly = ticketsSettingsDTO.toJS();
        return new Promise((resolve /*, reject*/) => {
            if (ticketSettingsKey) {
                dispatch(
                    updateTicketsSettingsForQueue(
                        ticketSettingsKey,
                        queueEntityKey,
                        ticketSettingsDTOApiFriendly,
                    ),
                ).finally(() => {
                    resolve();
                });
            } else {
                dispatch(
                    createTicketsSettingsForQueue(
                        queueEntityKey,
                        ticketSettingsDTOApiFriendly,
                    ),
                ).finally(() => {
                    resolve();
                });
            }
        });
    };

export const fetchOpenTicketsAssignedToUserLatest = (dispatch, getState) =>
    dispatch(
        fetchTicketsListPage({
            pageNumber: 0,
            nextPageNumber: undefined,
            assignee: selectOwnExtensionKey(getState()),
            state: 'open',
            setData: setOpenTicketsAssignedToUserData,
        }),
    );

export const fetchOpenTicketsAssignedToUserNext = (dispatch, getState) => {
    const pageNumber =
        selectOpenTicketsAssignedToLoggedInUserRequestPageNumber(getState());
    return dispatch(
        fetchTicketsListPage({
            pageNumber,
            nextPageNumber: pageNumber + 1,
            assignee: selectOwnExtensionKey(getState()),
            state: 'open',
            setData: setOpenTicketsAssignedToUserData,
        }),
    );
};

export const fetchOpenTicketsUnassignedLatest = (dispatch) =>
    dispatch(
        fetchTicketsListPage({
            pageNumber: 0,
            nextPageNumber: undefined,
            assignee: 'unassigned',
            state: 'open',
            setData: setOpenTicketsUnassignedData,
        }),
    );

export const fetchOpenTicketsUnassignedNext = (dispatch, getState) => {
    const pageNumber = selectOpenTicketsUnassignedRequestPageNumber(getState());
    return dispatch(
        fetchTicketsListPage({
            pageNumber,
            nextPageNumber: pageNumber + 1,
            assignee: 'unassigned',
            state: 'open',
            setData: setOpenTicketsUnassignedData,
        }),
    );
};

export const fetchTicketsAllLatest = (dispatch) =>
    dispatch(
        fetchTicketsListPage({
            pageNumber: 0,
            nextPageNumber: undefined,
            assignee: undefined,
            state: undefined,
            setData: setTicketsAllData,
        }),
    );

export const fetchTicketsAllNext = (dispatch, getState) => {
    const pageNumber = selectTicketsAllStatesRequestPageNumber(getState());
    return dispatch(
        fetchTicketsListPage({
            pageNumber,
            nextPageNumber: pageNumber + 1,
            assignee: undefined,
            state: undefined,
            setData: setTicketsAllData,
        }),
    );
};

export const fetchTicketsRelatedCount = (ticketKey) => (dispatch) => {
    return new Promise((resolve, reject) => {
        TicketsResource.countRelatedTickets(
            {
                onResult: (json) => {
                    const count = parseInt(json, 10);
                    dispatch(setTotalNumberOfRelatedTickets(ticketKey, count));
                    resolve();
                },
                onClientError: () => {
                    reject();
                },
                onError: () => {
                    networkErrorDispatcher(dispatch);
                    reject();
                },
            },
            ticketKey,
        );
    });
};

export const fetchRelatedTicketsLatest =
    (ticketKey) => (dispatch, getState) => {
        const { sortField, sortDirection } =
            selectTicketsSortForApi(getState());
        return new Promise((resolve, reject) => {
            TicketsResource.fetchRelatedTickets(
                {
                    onResult(json) {
                        const apiHasMoreData =
                            json.length === TICKETS_PAGE_SIZE;
                        dispatch(
                            setTicketsRelatedData(
                                ticketKey,
                                json,
                                undefined,
                                apiHasMoreData,
                            ),
                        );
                        resolve();
                    },
                    onClientError: (/* error */) => {
                        reject();
                    },
                    onError: () => {
                        networkErrorDispatcher(dispatch);
                        reject();
                    },
                },
                ticketKey,
                TICKETS_PAGE_SIZE,
                0,
                sortField,
                sortDirection,
            );
        });
    };

export const fetchRelatedTicketsNext = (ticketKey) => (dispatch, getState) => {
    const pageNumber = selectRelatedTicketsRequestPageNumber(
        getState(),
        ticketKey,
    );
    const { sortField, sortDirection } = selectTicketsSortForApi(getState());
    return new Promise((resolve, reject) => {
        TicketsResource.fetchRelatedTickets(
            {
                onResult(json) {
                    const nextPageNumber = pageNumber + 1;
                    const apiHasMoreData = json.length === TICKETS_PAGE_SIZE;
                    dispatch(
                        setTicketsRelatedData(
                            ticketKey,
                            json,
                            nextPageNumber,
                            apiHasMoreData,
                        ),
                    );
                    resolve();
                },
                onClientError: (/*error*/) => {
                    reject();
                },
                onError: () => {
                    networkErrorDispatcher(dispatch);
                    reject();
                },
            },
            ticketKey,
            TICKETS_PAGE_SIZE,
            pageNumber,
            sortField,
            sortDirection,
        );
    });
};

export const fetchArchivedTicketsNext = (dispatch, getState) => {
    const pageNumber = selectClosedTicketsRequestPageNumber(getState());
    return dispatch(
        fetchTicketsListPage({
            pageNumber,
            nextPageNumber: pageNumber + 1,
            assignee: undefined,
            state: 'closed',
            setData: setClosedTickets,
        }),
    );
};

export const fetchArchivedTicketsLatest = (dispatch) =>
    dispatch(
        fetchTicketsListPage({
            pageNumber: 0,
            nextPageNumber: undefined,
            assignee: undefined,
            state: 'closed',
            setData: setClosedTickets,
        }),
    );

/**
 * Sends a partial update, carrying only the fields named in the patch.
 *
 * This is how a single-field change should go out. The alternative is sending the
 * whole ticket back, which replaces it wholesale and so reverts anything a
 * colleague changed while we held our copy — last write wins, on fields we never
 * touched. Assignee and state are the two a queue's agents change at the same
 * moment, so they are where it bites.
 */
const patchTicket = (ticketKey, patch) => (dispatch) => {
    return new Promise((resolve, reject) => {
        TicketsResource.patchTicket(
            {
                onResult(json) {
                    dispatch(setTicket(ticketKey, json));
                    resolve(json);
                },
                onClientError: (error) => {
                    dispatch(onTicketWriteRefused(ticketKey, error));
                    reject(error);
                },
                onError: (error) => {
                    networkErrorDispatcher(dispatch);
                    reject(error);
                },
            },
            ticketKey,
            patch,
        );
    });
};

// Unassigning has its own flag because the payload cannot say it any other way:
// an omitted key and an explicit null both arrive at the backend as absent, which
// means leave the assignee alone.
export const updateTicketAssignee = (ticketKey, newAssignee) => (dispatch) =>
    dispatch(
        patchTicket(
            ticketKey,
            newAssignee === 'unassigned'
                ? { clearAssignee: true }
                : { assignee: newAssignee },
        ),
    );

export const updateTicketState = (ticketKey, newTicketState) => (dispatch) =>
    dispatch(patchTicket(ticketKey, { state: newTicketState }));

export const updateTicketVisibility =
    (ticketKey, newVisibility) => (dispatch) =>
        dispatch(patchTicket(ticketKey, { visibility: newVisibility }));

export const updateTicketSubject = (ticketKey, subject) => (dispatch) =>
    dispatch(patchTicket(ticketKey, { subject }));

export const fetchTicketsTabIsEnforcedForUser = () => (dispatch) => {
    return new Promise((resolve, reject) => {
        TicketsResource.getDisplayTicketTab({
            onResult(value) {
                dispatch(setTicketsTabIsEnforcedForUser(value));
                resolve(value);
            },
            onClientError: () => {
                reject();
            },
            onError: () => {
                networkErrorDispatcher(dispatch);
                reject();
            },
        });
    });
};

export const removeTicketMember =
    (ticketKey, extensionKey) => (dispatch, getState) => {
        return new Promise((_resolve, reject) => {
            TicketsResource.removeMember(
                {
                    onResult(json) {
                        const userRemovedIsLoggedInUser =
                            getOwnExtensionKey(getState()) === extensionKey;
                        if (userRemovedIsLoggedInUser) {
                            dispatch(
                                onOwnUserRemovedFromTicket(
                                    ticketKey,
                                    true,
                                    [],
                                    [extensionKey],
                                ),
                            );
                        } else {
                            dispatch(setTicket(ticketKey, json));
                        }
                    },
                    onClientError: (error) => {
                        dispatch(onTicketWriteRefused(ticketKey, error));
                        reject(error);
                    },
                    onError: () => {
                        networkErrorDispatcher(dispatch);
                        reject();
                    },
                },
                ticketKey,
                extensionKey,
            );
        });
    };

export const fetchTicketsBadgeCount = () => (dispatch) => {
    return new Promise((resolve, reject) => {
        TicketsResource.getTicketsBadgeCount({
            onResult(json) {
                const count = parseInt(json, 10);
                dispatch(setTicketsBadgeCount(count));
                resolve();
            },
            onClientError: (error) => {
                reject(error);
            },
            onError: (error) => {
                networkErrorDispatcher(dispatch);
                reject(error);
            },
        });
    });
};

export const fetchTotalNumberOfTicketsOpenAssignedToLoggedInUser = (
    dispatch,
) => {
    return new Promise((resolve, reject) => {
        TicketsResource.getNumberOfOpenTicketsAssignedToUser({
            onResult(json) {
                const count = parseInt(json, 10);
                dispatch(
                    setTotalNumberOfTicketsOpenAssignedToLoggedInUser(count),
                );
                resolve();
            },
            onClientError: (/*error*/) => {
                reject();
            },
            onError: () => {
                networkErrorDispatcher(dispatch);
                reject();
            },
        });
    });
};

const spawnAssigneeSetNotificationIfRelevant =
    (
        ticketKey,
        ownExtensionKey,
        assigneeKey,
        actorKey,
        assigneeIsSetBySystem,
    ) =>
    (dispatch, getState) => {
        if (assigneeKey !== ownExtensionKey) {
            //Not the logged-in user that's been assigned; don't display notification
            return;
        }

        if (assigneeIsSetBySystem) {
            //At the time of writing this is true when the ticket is assigned by answering the call; don't display notification
            return;
        }

        if (actorKey === ownExtensionKey) {
            //The ticket's been assigned to the user by herself; don't display notification
            return;
        }

        const actorExtension = getExtension(getState(), actorKey);
        const actorName = actorExtension
            ? getContactName(actorExtension.get('contact'))
            : '';
        addTicketLogEntry(
            `Displaying notification for ${ticketKey} assigned to user`,
        );
        const ticketIdUIFriendly =
            getTicketIdUiFriendlyFromTicketKey(ticketKey);
        const title = i18next.t('ticket.assigned.to.you');
        const body = i18next.t('ticket.assigned.to.you.extended', {
            ticketId: ticketIdUIFriendly,
            actor: actorName,
        });
        dispatch(spawnTicketNotification(ticketKey, title, body));
    };

const modifyOpenTicketsAssignedToUserCountOnAssigneeChangeIfAppropriate =
    (assigneeNewKey, assigneePrevKey, ticketStateKey, ownExtensionKey) =>
    (dispatch) => {
        if (ticketStateKey !== 'open') {
            return;
        }

        if (assigneeNewKey === ownExtensionKey) {
            dispatch(incrementTicketsOpenAssignedToUserCount);
        } else if (assigneePrevKey === ownExtensionKey) {
            dispatch(decrementTicketsOpenAssignedToUserCount);
        }
    };

const modifyOpenTicketsAssignedToUserCountOnTicketStateChangeIfAppropriate =
    (ticketStateNewKey, ticketStatePrevKey, assigneeKey, ownExtensionKey) =>
    (dispatch) => {
        if (assigneeKey !== ownExtensionKey) {
            return;
        }

        if (ticketStateNewKey === 'open') {
            dispatch(incrementTicketsOpenAssignedToUserCount);
        } else if (ticketStatePrevKey === 'open') {
            dispatch(decrementTicketsOpenAssignedToUserCount);
        }
    };

const previousOrNewAssigneeIsLoggedInUser = (
    assigneePrevKey,
    assigneeNewKey,
    ownExtensionKey,
) => {
    return (
        assigneePrevKey === ownExtensionKey ||
        assigneeNewKey === ownExtensionKey
    );
};

const spawnTicketStatusSetToOpenNotificationIfRelevant =
    (
        ticketKey,
        ownExtensionKey,
        assigneeKey,
        ticketStateKey,
        actorKey,
        ticketStatusIsSetBySystem,
    ) =>
    (dispatch, getState) => {
        if (ticketStateKey !== 'open') {
            return;
        }

        if (assigneeKey !== ownExtensionKey) {
            //Not the logged-in user that is assigned; don't display notification
            return;
        }

        if (ticketStatusIsSetBySystem) {
            return;
        }

        if (actorKey === ownExtensionKey) {
            //The ticket's been opened by the logged-in user; don't display notification
            return;
        }

        const actorExtension = getExtension(getState(), actorKey);
        const actorName = actorExtension
            ? getContactName(actorExtension.get('contact'))
            : '';
        addTicketLogEntry(`Displaying notification for ${ticketKey} re-opened`);
        const ticketIdUIFriendly =
            getTicketIdUiFriendlyFromTicketKey(ticketKey);

        const title = i18next.t('ticket.reopened');

        const body = i18next.t('ticket.reopened.body.extended', {
            ticketId: ticketIdUIFriendly,
            actor: actorName,
        });

        dispatch(spawnTicketNotification(ticketKey, title, body));
    };

export const onTopEventPushTypeTicketsBadgeCount = (topData) => (dispatch) => {
    addTicketLogEntry(`tickets_badge_count: count ${topData.data.count}`);
    const count = parseInt(topData.data.count, 10);
    dispatch(setTicketsBadgeCount(count));
};

export const onTopEventPushOfTypeTicketAssigneeSet =
    (topData) => (dispatch, getState) => {
        addTicketLogEntry(
            `Top event ticket_assignee_set: ticketKey ${topData.data.ticketKey}, new ${topData.data.newValue}, prev ${topData.data.prevValue}, actor ${topData.data.actor}`,
        );
        const ticketKey = topData.data.ticketKey;
        const assigneeKey = topData.data.newValue;
        const actorKey = topData.data.actor;
        const assigneeSetBySystem = !actorKey; //When the ticket is assigned by answering the call
        const ownExtensionKey = getOwnExtensionKey(getState());
        const ticketFromAppState = selectTicket(getState(), ticketKey);

        const lastUpdate = topData.data.ticketLastUpdate;
        if (lastUpdate) {
            dispatch(setTicketLastUpdate(ticketKey, lastUpdate));
        }

        if (ticketFromAppState) {
            const timestamp = topData.data.date;
            const assigneeChangedEventDto = {
                DTOSubtype: 'OmniAssigneeChangedEvent',
                key: topData.data.actionKey,
                newValue: assigneeKey,
                prevValue: topData.data.prevValue,
                actor: actorKey || 'SYSTEM',
                timeStamp: timestamp,
            };
            dispatch(addTicketEvent(assigneeChangedEventDto, ticketKey));

            dispatch(
                setTicketAssignee(ticketKey, assigneeKey, ownExtensionKey),
            );
            const ticketState = ticketFromAppState.get('state');
            dispatch(
                modifyOpenTicketsAssignedToUserCountOnAssigneeChangeIfAppropriate(
                    assigneeKey,
                    topData.data.prevValue,
                    ticketState,
                    ownExtensionKey,
                ),
            );
            dispatch(
                spawnAssigneeSetNotificationIfRelevant(
                    ticketKey,
                    ownExtensionKey,
                    assigneeKey,
                    actorKey,
                    assigneeSetBySystem,
                ),
            );
            return;
        }

        if (
            !previousOrNewAssigneeIsLoggedInUser(
                topData.data.prevValue,
                assigneeKey,
                ownExtensionKey,
            )
        ) {
            //No need to fetch ticket if it doesn't affect logged-in user
            return;
        }

        setTimeout(() => {
            //Sometimes the data in the ticket received from the API has not been updated when fetching
            //right after the top event is received, hence this timeout, of arbitrarily chosen 1 second
            dispatch(fetchTicket(ticketKey)).then((ticketData) => {
                if (ticketData.assignee !== assigneeKey) {
                    addTicketLogEntry(
                        `Ticket assignee ${assigneeKey} from Top mismatch with data fetched from API: ${ticketData.assignee}. TicketID: ${ticketKey}`,
                    );
                }
                const ticketState = ticketData.state;
                dispatch(
                    modifyOpenTicketsAssignedToUserCountOnAssigneeChangeIfAppropriate(
                        assigneeKey,
                        topData.data.prevValue,
                        ticketState,
                        ownExtensionKey,
                    ),
                );
                dispatch(
                    spawnAssigneeSetNotificationIfRelevant(
                        ticketKey,
                        ownExtensionKey,
                        assigneeKey,
                        actorKey,
                        assigneeSetBySystem,
                    ),
                );
            });
        }, ONE_SECOND_MILLISECONDS);
    };

export const onTopEventPushOfTypeTicketStateSet =
    (topData) => (dispatch, getState) => {
        addTicketLogEntry(
            `Top event ticket_status_set: ticketKey ${topData.data.ticketKey}, new ${topData.data.newValue}, prev ${topData.data.prevValue}, actor ${topData.data.actor}`,
        );
        const ticketKey = topData.data.ticketKey;
        const stateKey = topData.data.newValue;
        const actorKey = topData.data.actor;
        const ticketStatusIsSetBySystem = !actorKey;
        const ownExtensionKey = getOwnExtensionKey(getState());
        addTicketLogEntry(`${ticketKey} set to ${stateKey} by ${actorKey}`);
        const ticketFromAppState = selectTicket(getState(), ticketKey);

        const lastUpdate = topData.data.ticketLastUpdate;
        if (lastUpdate) {
            dispatch(setTicketLastUpdate(ticketKey, lastUpdate));
        }

        if (ticketFromAppState) {
            const timestamp = topData.data.date;
            const stateChangedEventDto = {
                DTOSubtype: 'OmniStateChangedEvent',
                key: topData.data.actionKey,
                newValue: stateKey,
                prevValue: topData.data.prevValue,
                actor: actorKey || 'SYSTEM',
                timeStamp: timestamp,
            };
            dispatch(addTicketEvent(stateChangedEventDto, ticketKey));

            dispatch(setTicketState(ticketKey, stateKey, ownExtensionKey));

            const assigneeKey = ticketFromAppState.get('assignee');
            dispatch(
                modifyOpenTicketsAssignedToUserCountOnTicketStateChangeIfAppropriate(
                    stateKey,
                    topData.data.prevValue,
                    assigneeKey,
                    ownExtensionKey,
                ),
            );
            dispatch(
                spawnTicketStatusSetToOpenNotificationIfRelevant(
                    ticketKey,
                    ownExtensionKey,
                    assigneeKey,
                    stateKey,
                    actorKey,
                    ticketStatusIsSetBySystem,
                ),
            );
            return;
        }

        if (stateKey !== 'open') {
            return;
        }

        //Sometimes the data in the ticket received from the API has not been updated when fetching
        //right after the top event is received, hence this timeout, of arbitrarily chosen 1 second
        setTimeout(() => {
            dispatch(fetchTicket(ticketKey)).then((ticketData) => {
                if (ticketData.state !== stateKey) {
                    addTicketLogEntry(
                        `Ticket state ${stateKey} from Top mismatch with data fetched from API: ${ticketData.state}. TicketID: ${ticketKey}`,
                    );
                }
                const assigneeKey = ticketData.assignee;
                dispatch(
                    modifyOpenTicketsAssignedToUserCountOnTicketStateChangeIfAppropriate(
                        stateKey,
                        topData.data.prevValue,
                        assigneeKey,
                        ownExtensionKey,
                    ),
                );
                dispatch(
                    spawnTicketStatusSetToOpenNotificationIfRelevant(
                        ticketKey,
                        ownExtensionKey,
                        assigneeKey,
                        stateKey,
                        actorKey,
                        ticketStatusIsSetBySystem,
                    ),
                );
            });
        }, ONE_SECOND_MILLISECONDS);
    };

const getSenderDataFromSender = (sender) => {
    return {
        sender: sender,
        senderType: isExtensionKey(sender) ? 'ACCOUNT' : 'EXTERNAL',
    };
};

export const onTopEventPushOfTypeTicketSmsReceived =
    (topData) => (dispatch, getState) => {
        addTicketLogEntry(
            `Top event ticket_new_sms_note: ticketKey ${topData.data.ticketKey}, messageKey ${topData.data.messageKey}, actor ${topData.data.actor}`,
        );
        const ticketKey = topData.data.ticketKey;
        const message = topData.data.message;
        const sender = topData.data.actor;
        const timestamp = topData.data.date;
        const messageKey = topData.data.messageKey;

        const internalNoteEventDto = {
            DTOSubtype: 'OmniSmsNoteTicketEvent',
            message: message,
            senderData: getSenderDataFromSender(sender),
            timeStamp: timestamp,
            read: true,
            key: messageKey,
        };

        const actorIsOwnExtension =
            sender === selectOwnExtensionKey(getState());

        const lastUpdate = topData.data.ticketLastUpdate;
        if (lastUpdate) {
            dispatch(applyPushedTicketLastUpdate(ticketKey, lastUpdate));
        }

        dispatch(
            addTicketEvent(
                internalNoteEventDto,
                ticketKey,
                actorIsOwnExtension,
                'TOP',
            ),
        );
    };

export const onTopEventPushOfTypeTicketInternalNoteReceived =
    (topData) => (dispatch, getState) => {
        addTicketLogEntry(
            `Top event ticket_new_internal_note: ticketKey ${topData.data.ticketKey}, messageKey ${topData.data.messageKey}, actor ${topData.data.actor}`,
        );
        const ticketKey = topData.data.ticketKey;
        const message = topData.data.message;
        const sender = topData.data.actor;
        const timestamp = topData.data.date;
        const messageKey = topData.data.messageKey;
        const mentionedAccounts = topData.data.mentionedAccounts || [];

        const internalNoteEventDto = {
            DTOSubtype: 'OmniInternalNoteTicketEvent',
            message: message,
            senderData: getSenderDataFromSender(sender),
            timeStamp: timestamp,
            read: true,
            key: messageKey,
            numberOfAttachments: topData.data.numberOfAttachments,
        };

        const actorIsOwnExtension =
            sender === selectOwnExtensionKey(getState());

        const lastUpdate = topData.data.ticketLastUpdate;
        if (lastUpdate) {
            dispatch(applyPushedTicketLastUpdate(ticketKey, lastUpdate));
        }

        dispatch(
            addTicketEvent(
                internalNoteEventDto,
                ticketKey,
                actorIsOwnExtension,
                'TOP',
            ),
        );
        // The push carries no stylings, so its mentions show as plain text until
        // the note is fetched with them. Only a pushed copy needs that: the
        // sending client already has the note with its stylings from the send
        // response, or has dropped the push while that response is on its way.
        if (
            mentionedAccounts.length > 0 &&
            selectTicketEventsIsMounted(getState(), ticketKey) &&
            selectMessageEventLacksStylings(getState(), ticketKey, messageKey)
        ) {
            dispatch(fetchTicketMessage(ticketKey, messageKey));
        }
        dispatch(
            displayMentionNotificationIfAppropriate(
                ticketKey,
                sender,
                message,
                mentionedAccounts,
            ),
        );
    };

const getIconForTicketEventDtoSubtype = (dtoSubtype) => {
    if (!dtoSubtype) return null;
    if (dtoSubtype === 'OmniWhatsAppMessageTicketEvent') return icon_whatsapp;
    if (dtoSubtype === 'OmniWhatsAppTemplateMessageTicketEvent')
        return icon_whatsapp;
    if (dtoSubtype === 'OmniWhatsAppUnsupportedTicketEvent')
        return icon_whatsapp;
    if (dtoSubtype === 'OmniEmailMessageTicketEvent') return icon_email;
    return null;
};

const onTicketMessageTopEventReceived =
    (topData, dtoSubtype) => (dispatch, getState) => {
        const sender = topData.data.actor;
        const message = topData.data.message;
        const eventDTO = {
            DTOSubtype: dtoSubtype,
            direction: topData.data.direction,
            key: topData.data.messageKey,
            message: message,
            senderData: getSenderDataFromSender(sender),
            timeStamp: topData.data.date,
            numberOfAttachments: topData.data.numberOfAttachments || 0,
        };

        const ownExtensionKey = selectOwnExtensionKey(getState());
        const actorIsOwnExtension = sender === ownExtensionKey;
        const ticketKey = topData.data.ticketKey;
        const isEmailMessage = dtoSubtype === 'OmniEmailMessageTicketEvent';

        const lastUpdate = topData.data.ticketLastUpdate;
        if (lastUpdate) {
            dispatch(applyPushedTicketLastUpdate(ticketKey, lastUpdate));
        }

        if (!isEmailMessage) {
            // We add email messages to the store only when we fetch the full message data after receiving the Top event,
            // to avoid having the email message stored as plain text (e.g. without the HTML version of the message, which is not included in the Top message).
            // For other message types, we can add them to the store right away when receiving the top event.
            dispatch(
                addTicketEvent(eventDTO, ticketKey, actorIsOwnExtension, 'TOP'),
            );
        }

        const state = getState();

        if (selectTicketEventsIsMounted(state, ticketKey)) {
            if (isEmailMessage) {
                // If it's an email message, we fetch full message data so that we receive the HTML version of the message
                dispatch(
                    fetchTicketMessage(ticketKey, topData.data.messageKey),
                );
            }

            if (selectAppIsVisibleAndFocused(state)) return;
            // If the view of the ticket in question is open and the app is in focus, we return, not moving on to the
            // notification spawning logic, as we do not want to display a notification in this case.
        }

        if (actorIsOwnExtension) return; // Action performed by the logged-in user, no need to display notification
        if (topData.data.ticketAssignee !== ownExtensionKey) return; // Logged-in user is not assigned to the ticket, no need to display notification
        if (topData.data.direction !== 'INCOMING') return; // Message is outgoing (e.g. from a colleague or system welcome/closed message; no need to display notification)

        const title = i18next.t('tickets.message.from', {
            sender: prettyFormatE164Number(
                sender,
                selectUserCountryCode(state),
            ),
        });
        dispatch(
            spawnTicketNotification(
                ticketKey,
                title,
                message,
                getIconForTicketEventDtoSubtype(dtoSubtype),
            ),
        );
    };

export const onTopEventPushTypeTicketEmailMessageReceived =
    (topData) => (dispatch) => {
        addTicketLogEntry(
            `Top event ticket_new_email_message. messageKey: ${topData.data.messageKey}`,
        );
        dispatch(
            onTicketMessageTopEventReceived(
                topData,
                'OmniEmailMessageTicketEvent',
            ),
        );
    };

export const onTopEventPushOfTypeTicketWhatsAppMessageReceived =
    (topData) => (dispatch) => {
        addTicketLogEntry(
            `Top event ticket_new_whatsapp_message. messageKey: ${topData.data.messageKey}`,
        );
        dispatch(
            onTicketMessageTopEventReceived(
                topData,
                'OmniWhatsAppMessageTicketEvent',
            ),
        );
    };

export const onTopEventTicketWhatsAppTemplateMessage =
    (topData) => (dispatch) => {
        addTicketLogEntry(
            `Top event ticket_new_whatsapp_template_message. messageKey: ${topData.data.messageKey}`,
        );
        dispatch(
            onTicketMessageTopEventReceived(
                topData,
                'OmniWhatsAppTemplateMessageTicketEvent',
            ),
        );
    };

export const onTopEventPushOfTypeTicketWhatsAppUnsupportedMessage =
    (topData) => (dispatch) => {
        addTicketLogEntry(
            `Top event ticket_unsupported_whatsapp_message. messageKey: ${topData.data.messageKey}`,
        );
        dispatch(
            onTicketMessageTopEventReceived(
                topData,
                'OmniWhatsAppUnsupportedTicketEvent',
            ),
        );
    };

export const displayMentionNotificationIfAppropriate =
    (ticketKey, sender, message, mentionees = []) =>
    (dispatch, getState) => {
        if (mentionees.length === 0) return;
        if (selectTicketEventsIsMounted(getState(), ticketKey)) return; // Not displaying notification if ticket view is open

        const ownExtensionKey = selectOwnExtensionKey(getState());
        if (!mentionees.some((m) => m.key === ownExtensionKey)) return;

        const ticketIdUserFriendly =
            getTicketIdUiFriendlyFromTicketKey(ticketKey);
        const title = i18next.t('tickets.ticket_id', {
            ticketId: ticketIdUserFriendly,
        });
        const senderName = getExtensionContactFirstNameLastName(
            getState(),
            sender,
        );
        const senderNameUIFriendly =
            getStringWithFirstLetterFromSecondWord(senderName);
        const body = i18next.t('message.name_message', {
            name: senderNameUIFriendly,
            message: message,
        });

        dispatch(spawnTicketNotification(ticketKey, title, body));
    };

export const onTopEventPushOfTypeTicketCreatedReceived =
    (topData) => (dispatch, getState) => {
        addTicketLogEntry(
            `Top event ticket_new_ticket: ticketKey ${topData.data.ticketKey}, originKey ${topData.data.originKey}, locutusSessionId ${topData.data.locutusSessionId}`,
        );
        if (!selectTicketsTabIsEnforced(getState())) {
            dispatch(setTicketsTabIsEnforcedForUser(true));
        }

        const ticketKey = topData.data.ticketKey;
        const locutusSessionId = topData.data.locutusSessionId;
        const originKey = topData.data.originKey;

        if (locutusSessionId) {
            dispatch({
                type: Actions.SET_TICKET_KEY_FOR_LOCUTUS_SESSION,
                locutusSessionId,
                ticketKey,
            });
        }

        const lastUpdate = topData.data.ticketLastUpdate;
        if (lastUpdate) {
            dispatch(setTicketLastUpdate(ticketKey, lastUpdate));
        }

        const ticketsTabIsMounted = !!selectTicketsListIsMounted(getState());
        const pbxPageForOriginKeyIsMounted = isTypeQueueKey(originKey)
            ? selectPbxPageIsMounted(getState(), originKey)
            : false;

        if (pbxPageForOriginKeyIsMounted) {
            dispatch(
                setTicketKeyForQueueCallRecord(
                    originKey,
                    `call-${locutusSessionId}`,
                    ticketKey,
                ),
            );
        }

        if (ticketsTabIsMounted) {
            setTimeout(() => {
                if (!selectTicketIsFetched(getState(), ticketKey)) {
                    dispatch(fetchTicket(ticketKey));
                }
                // About if block above:
                // If we've already fetched the ticket (e.g. via the TicketListItem),
                // we don't need to fetch it again here.
            }, ONE_SECOND_MILLISECONDS);
        }
    };

const setTicketKeyForQueueCallRecord =
    (queueKey, callKey, ticketKey) => (dispatch) => {
        dispatch({
            type: Actions.SET_OMNI_TICKET_KEY_QUEUE_CALL,
            queueKey,
            callKey,
            ticketKey,
        });
    };

export const onTopEventTicketMessageStatusSet = (topData) => (dispatch) => {
    const ticketKey = topData.data.ticketKey;

    const lastUpdate = topData.data.ticketLastUpdate;
    if (lastUpdate) {
        dispatch(applyPushedTicketLastUpdate(ticketKey, lastUpdate));
    }

    dispatch(
        setTicketMessageStatus(
            ticketKey,
            topData.data.messageKey,
            topData.data.newValue,
        ),
    );
};

export const setTicketMessageStatus =
    (ticketKey, messageKey, messageStatus) => (dispatch) =>
        dispatch({
            type: Actions.SET_TICKET_EVENT_MESSAGE_STATUS,
            ticketKey,
            messageKey,
            messageStatus,
        });

export const onTopEventPushOfTypeTicketMembersChanged =
    (topData) => (dispatch, getState) => {
        const ticketKey = topData.data.ticketKey;
        const membersAdded = topData.data.addedExtensions || [];
        const membersRemoved = topData.data.removedExtensions || [];
        const actor = topData.data.actor;
        addTicketLogEntry(
            `Top event ticket_members_change: ticketKey ${ticketKey}, added ${membersAdded.length}, removed ${membersRemoved.length}, actor ${actor}`,
        );
        const ownExtensionKey = getOwnExtensionKey(getState());

        const lastUpdate = topData.data.ticketLastUpdate;
        if (lastUpdate) {
            dispatch(setTicketLastUpdate(ticketKey, lastUpdate));
        }

        if (membersAdded.includes(ownExtensionKey)) {
            //Logged-in user added to ticket, fetch relevant tickets data
            if (!selectTicketsTabIsEnforced(getState())) {
                dispatch(setTicketsTabIsEnforcedForUser(true));
                dispatch(fetchTicketsBadgeCount());
            }

            setTimeout(() => {
                if (qualifiesForTicketFetch(getState(), ticketKey)) {
                    dispatch(fetchTicket(ticketKey));
                }
            }, ONE_SECOND_MILLISECONDS);
        } else if (membersRemoved.includes(ownExtensionKey)) {
            // Dispatched even when the logged-in user is the actor, because a
            // second instance of the app has no API callback of its own and would
            // otherwise keep the ticket. Whichever report arrives first removes it
            // and navigates; the other finds it gone and does nothing.
            const actorIsOwnUser = actor === ownExtensionKey;
            dispatch(
                onOwnUserRemovedFromTicket(
                    ticketKey,
                    actorIsOwnUser,
                    membersAdded,
                    membersRemoved,
                ),
            );
        } else {
            dispatch(
                applyTicketMembersChange(
                    ticketKey,
                    membersAdded,
                    membersRemoved,
                ),
            );
        }
    };

const getBooleanValue = (value) =>
    typeof value === 'boolean' ? value : value === 'true';
export const onTopEventTicketAwaitingActivitySet =
    (topData) => (dispatch, getState) => {
        addTicketLogEntry(
            `Top event ticket_awaiting_activity_set: ticketKey ${topData.data.ticketKey}, newValue ${topData.data.newValue}`,
        );
        const ticketKey = topData.data.ticketKey;
        const newValueBoolean = getBooleanValue(topData.data.newValue);
        const ownExtensionKey = selectOwnExtensionKey(getState());

        const lastUpdate = topData.data.ticketLastUpdate;
        if (lastUpdate) {
            dispatch(setTicketLastUpdate(ticketKey, lastUpdate));
        }

        if (
            topData.data.ticketAssignee === ownExtensionKey &&
            qualifiesForTicketFetch(getState(), ticketKey)
        ) {
            dispatch(fetchTicket(ticketKey));
        } else {
            dispatch(setAwaitingActivity(ticketKey, newValueBoolean));
        }
    };

export const onTopEventTicketSubjectSet = (topData) => (dispatch, getState) => {
    const ticketKey = topData.data.ticketKey;

    const lastUpdate = topData.data.ticketLastUpdate;
    if (lastUpdate) {
        dispatch(applyPushedTicketLastUpdate(ticketKey, lastUpdate));
    }

    dispatch(setTicketSubject(ticketKey, topData.data.newValue));

    const ticketFromAppState = selectTicket(getState(), ticketKey);
    if (ticketFromAppState) {
        const timestamp = topData.data.date;
        const subjectChangedEventDto = {
            DTOSubtype: 'OmniSubjectChangedEvent',
            key: topData.data.actionKey,
            newValue: topData.data.newValue,
            prevValue: topData.data.prevValue,
            actor: topData.data.actor || 'SYSTEM',
            timeStamp: timestamp,
        };
        dispatch(addTicketEvent(subjectChangedEventDto, ticketKey));
    }
};

// No action is recorded for this change, so there is no timeline event to add.
// Every recipient is a member, the assignee or the actor, so a narrowing tier
// never costs them access — nothing here has to fetch or drop the ticket.
export const onTopEventTicketVisibilitySet = (topData) => (dispatch) => {
    addTicketLogEntry(
        `Top event ticket_visibility_set: ticketKey ${topData.data.ticketKey}, newValue ${topData.data.newValue}`,
    );
    const ticketKey = topData.data.ticketKey;

    const lastUpdate = topData.data.ticketLastUpdate;
    if (lastUpdate) {
        dispatch(applyPushedTicketLastUpdate(ticketKey, lastUpdate));
    }

    const visibility = ticketVisibilityFromWire(topData.data.newValue);
    if (visibility) {
        dispatch(setTicketVisibility(ticketKey, visibility));
    }
};

export const onTopEventTicketTranscriptionProgress =
    (topData) => (dispatch, getState) => {
        addTicketLogEntry(
            `Top event ticket_transcription_progress: ticketKey ${topData.data.ticketKey}, state ${topData.data.transcriptionState}, actor ${topData.data.actor}`,
        );
        const ticketKey = topData.data.ticketKey;
        const actor = topData.data.actor;
        const transcriptionState = topData.data.transcriptionState;

        const lastUpdate = topData.data.ticketLastUpdate;
        if (lastUpdate) {
            dispatch(applyPushedTicketLastUpdate(ticketKey, lastUpdate));
        }

        if (transcriptionState !== 'completed') return;

        // The feed refresh and the notification are the whole point of this event now: the
        // in-progress and scroll-to-recording flags it used to set had no readers left once the
        // ticket call card started rendering from the shared call-record components.
        if (selectTicketEventsIsMounted(getState(), ticketKey)) {
            dispatch(fetchTicketEvents(ticketKey));
            return;
        }

        if (actor === selectOwnExtensionKey(getState())) {
            dispatch(
                spawnTicketNotification(
                    ticketKey,
                    i18next.t('ai.transcribe.completed_notification'),
                    i18next.t('ai.transcribe.completed_click_view'),
                ),
            );
        }
    };

export const updateAwaitingActivity = (ticketKey, value) => (dispatch) =>
    dispatch(patchTicket(ticketKey, { awaitingActivity: value }));

export const refreshTicketsData = () => async (dispatch, getState) => {
    const state = getState();

    if (!selectShouldSeeTicketsTab(state)) {
        return;
    }

    dispatch(clearTicketsData);
    dispatch(fetchTicketsBadgeCount());
    await dispatch(fetchOpenTicketsAssignedToUserLatest);

    const ticketsListIsMounted = selectTicketsListIsMounted(state);

    if (ticketsListIsMounted) {
        dispatch(fetchTotalNumberOfTicketsOpenAssignedToLoggedInUser);

        const tabSelected = selectTicketsListTabSelected(state);
        switch (tabSelected) {
            case TICKETS_LIST_TABS_KEYS.UNASSIGNED:
                await dispatch(fetchOpenTicketsUnassignedLatest);
                break;
            case TICKETS_LIST_TABS_KEYS.ALL:
                await dispatch(fetchTicketsAllLatest);
                break;
            default:
                break;
        }
    }

    const ticketKeysMounted = selectTicketViewsMountedData(state);
    if (ticketKeysMounted && !ticketKeysMounted.isEmpty()) {
        for (const ticketKey of ticketKeysMounted.keys()) {
            const ticketHasBeenFetched = selectTicketIsFetched(
                state,
                ticketKey,
            );
            if (!ticketHasBeenFetched) {
                await dispatch(fetchTicket(ticketKey));
            }
            dispatch(fetchTicketEvents(ticketKey));
        }
    }
};
export const removeOpenAiExperimentalTagFromTicket =
    (ticketKey, tag) => (dispatch) => {
        dispatch({
            type: Actions.REMOVE_TICKET_OPEN_AI_EXPERIMENTAL_TAG,
            tag,
            ticketKey,
        });
    };

const setTotalNumberOfRelatedTickets = (ticketKey, count) => (dispatch) => {
    dispatch({
        type: Actions.SET_TOTAL_RELATED_TICKETS_COUNT,
        count,
        ticketKey,
    });
};

const setTicket = (ticketKey, ticketDTO) => (dispatch) => {
    dispatch({
        type: Actions.UPDATE_TICKET,
        ticketKey,
        ticketDTO,
    });
    dispatch({
        type: Actions.UPDATE_TICKET_TEMP_SEARCH_DATA,
        ticketKey,
        ticketDTO,
    });
};

const setClosedTickets =
    (data, nextPageNumber, apiHasMoreData) => (dispatch) => {
        dispatch({
            type: Actions.SET_CLOSED_TICKETS_DATA,
            data,
            nextPageNumber,
            apiHasMoreData,
        });
    };

const setOpenTicketsAssignedToUserData =
    (data, nextPageNumber, apiHasMoreData) => (dispatch) => {
        dispatch({
            type: Actions.SET_OPEN_TICKETS_ASSIGNED_USER_DATA,
            data,
            nextPageNumber,
            apiHasMoreData,
        });
    };

const setOpenTicketsUnassignedData =
    (data, nextPageNumber, apiHasMoreData) => (dispatch) => {
        dispatch({
            type: Actions.SET_OPEN_TICKETS_UNASSIGNED_DATA,
            data,
            nextPageNumber,
            apiHasMoreData,
        });
    };

const setTicketsAllData =
    (data, nextPageNumber, apiHasMoreData) => (dispatch) => {
        dispatch({
            type: Actions.SET_TICKETS_ALL_DATA,
            data,
            nextPageNumber,
            apiHasMoreData,
        });
    };

const setTicketData = (data, ownExtensionKey) => (dispatch) => {
    dispatch({
        type: Actions.SET_TICKET_DATA,
        data,
        ownExtensionKey,
    });
};

const setTicketEvents = (data, ticketKey) => (dispatch) => {
    dispatch({
        type: Actions.SET_TICKET_EVENTS,
        ticketKey,
        data,
    });

    // Consolidated call events (v3) carry a full CallRecordDTO. Upsert it into the canonical
    // call-records store so the ticket call card renders from the same data/selectors as the
    // call record page. The include scope tells the reducer which fields this payload is
    // authoritative for, so a recording/contact/action removed server-side clears here too
    // instead of lingering from an earlier richer fetch.
    data.forEach((event) => {
        if (
            eventIsCallRecordEvent(event?.DTOSubtype) &&
            event.callRecord?.key
        ) {
            dispatch({
                type: Actions.RESULT_CALL_RECORD,
                callRecordKey: event.callRecord.key,
                data: event.callRecord,
                include: CALL_RECORD_INCLUDES_TICKET_EVENT,
            });
        }
    });
};

const addTicketEvent =
    (eventData, ticketKey, actorIsOwnExtension, dataSource) => (dispatch) => {
        dispatch({
            type: Actions.ADD_TICKET_EVENT,
            ticketKey,
            eventData,
            actorIsOwnExtension,
            dataSource,
        });
    };

const setTicketAssignee =
    (ticketKey, assigneeKey, ownExtensionKey) => (dispatch) => {
        dispatch({
            type: Actions.SET_TICKET_ASSIGNEE,
            ticketKey,
            assigneeKey,
            ownExtensionKey,
        });
    };

const setTicketState =
    (ticketKey, ticketState, ownExtensionKey) => (dispatch) => {
        dispatch({
            type: Actions.SET_TICKET_STATE,
            ticketKey,
            ticketState,
            ownExtensionKey,
        });
    };

export const applyTicketMembersChange =
    (ticketKey, membersAdded, membersRemoved) => (dispatch) => {
        dispatch({
            type: Actions.APPLY_TICKET_MEMBERS_CHANGE,
            ticketKey,
            membersAdded,
            membersRemoved,
        });
    };

const incrementTicketsOpenAssignedToUserCount = (dispatch) => {
    dispatch({
        type: Actions.INCREMENT_TICKETS_OPEN_ASSIGNED_USER_COUNT_TOTAL,
    });
};

const decrementTicketsOpenAssignedToUserCount = (dispatch) => {
    dispatch({
        type: Actions.DECREMENT_TICKETS_OPEN_ASSIGNED_USER_COUNT_TOTAL,
    });
};

const setTotalNumberOfTicketsOpenAssignedToLoggedInUser =
    (count) => (dispatch) => {
        dispatch({
            type: Actions.SET_TICKETS_OPEN_ASSIGNED_USER_COUNT_TOTAL,
            count,
        });
    };

const setTicketsBadgeCount = (count) => (dispatch) => {
    dispatch({
        type: Actions.SET_TICKETS_BADGE_COUNT,
        count,
    });
};

export const setTicketsListMountStatus =
    (isMounted, tabSelected) => (dispatch) => {
        dispatch({
            type: Actions.SET_TICKETS_LIST_MOUNT_STATUS,
            isMounted,
            tabSelected,
        });
    };

export const setTicketViewMountStatus =
    (isMounted, ticketKey, context) => (dispatch) => {
        dispatch({
            type: Actions.SET_TICKET_VIEW_MOUNT_STATUS,
            isMounted,
            ticketKey,
            context,
        });
    };

export const setTicketEventsMountStatus =
    (ticketKey, isMounted) => (dispatch) => {
        dispatch({
            type: Actions.SET_TICKET_EVENTS_MOUNT_STATUS,
            isMounted,
            ticketKey,
        });
    };

const clearTicketsData = (dispatch) => {
    dispatch({
        type: Actions.CLEAR_TICKETS_DATA,
    });
};

const setTicketsSettingsInStoreForQueue =
    (queueEntityKey, ticketSettingsDTO) => (dispatch) => {
        dispatch({
            type: Actions.SET_TICKETS_SETTINGS_FOR_QUEUE,
            queueEntityKey: queueEntityKey,
            data: ticketSettingsDTO,
        });
    };

const setDefaultTicketVisibilityInStoreForQueue =
    (queueEntityKey, defaultTicketVisibilityDTO) => (dispatch) => {
        dispatch({
            type: Actions.SET_DEFAULT_TICKET_VISIBILITY_FOR_QUEUE,
            queueEntityKey: queueEntityKey,
            data: {
                ...defaultTicketVisibilityDTO,
                visibility: ticketVisibilityFromWire(
                    defaultTicketVisibilityDTO?.visibility,
                ),
            },
        });
    };

export const setTicketsTabIsEnforcedForUser = (value) => (dispatch) => {
    dispatch({
        type: Actions.SET_TICKETS_TAB_ENFORCED,
        value,
    });
};

export const fetchEmailSignaturesEnabled = () => (dispatch) => {
    return new Promise((resolve, reject) => {
        TicketsResource.getEmailSignaturesEnabled({
            onResult(value) {
                dispatch({
                    type: Actions.SET_EMAIL_SIGNATURES_ENABLED,
                    value,
                });
                resolve(value);
            },
            onClientError: () => {
                reject();
            },
            onError: () => {
                networkErrorDispatcher(dispatch);
                reject();
            },
        });
    });
};

export const setTicketsRelatedData =
    (ticketKey, data, nextPageNumber, apiHasMoreData) => (dispatch) => {
        dispatch({
            type: Actions.SET_TICKETS_RELATED_DATA,
            ticketKey,
            data,
            nextPageNumber,
            apiHasMoreData,
        });
    };

const setTagsAvailableForTicketTagging = (data) => (dispatch) => {
    dispatch({
        type: Actions.SET_ALL_TAGS_AVAILABLE_FOR_TICKET_TAGGING,
        data,
    });
};

const setTagRemovedFromTicket = (ticketKey, tagKey) => (dispatch) => {
    dispatch({
        type: Actions.REMOVE_TAG_FROM_TICKET,
        ticketKey,
        tagKey,
    });
};
const setRenameTag = (tagKey, newName) => (dispatch) => {
    dispatch({
        type: Actions.SET_RENAME_TAG,
        tagKey,
        newName,
    });
};

const setDeleteTagsCompletely = (tagKeys) => (dispatch) => {
    dispatch({
        type: Actions.DELETE_TAGS_COMPLETELY,
        tagKeys,
    });
};

const setNewTagCreated = (tagData) => (dispatch) => {
    dispatch({
        type: Actions.SET_NEW_TAG_CREATED,
        tagData,
    });
};

export const addTagToTicket =
    (ticketKey, label, tagKey, source) => (dispatch, getState) => {
        const tagDto = {
            ...(tagKey ? { key: tagKey } : {}),
            ...(source ? { source: source } : {}),
            ...(label ? { label: label } : {}),
        };

        return new Promise((resolve, reject) => {
            TicketsResource.addTag(
                {
                    onResult(json) {
                        const tagExistsInAvailableTags =
                            selectTagExistsInAvailableTags(
                                getState(),
                                tagKey,
                                label,
                            );
                        if (!tagExistsInAvailableTags) {
                            const tagAdded = json.tags?.find(
                                (tag) => tag.label === label,
                            );

                            if (tagAdded) {
                                dispatch(setNewTagCreated(tagAdded));
                            }
                        }

                        const ownExtensionKey = getOwnExtensionKey(getState());
                        dispatch(setTicketData(json, ownExtensionKey));
                        resolve(json);
                    },
                    onClientError: (error) => {
                        dispatch(onTicketWriteRefused(ticketKey, error));
                        reject(error);
                    },
                    onError: (error) => {
                        networkErrorDispatcher(dispatch);
                        reject(error);
                    },
                },
                ticketKey,
                tagDto,
            );
        });
    };

export const fetchAllAvailableTags = () => (dispatch) => {
    return new Promise((resolve, reject) => {
        TicketsResource.fetchAllTags({
            onResult(json) {
                dispatch(setTagsAvailableForTicketTagging(json));
                resolve(json);
            },
            onClientError: (error) => {
                reject(error);
            },
            onError: (error) => {
                networkErrorDispatcher(dispatch);
                reject(error);
            },
        });
    });
};

export const removeTagFromTicket =
    (ticketKey, tagKey, label) => (dispatch, getState) => {
        const keyOfTagToRemove =
            tagKey ||
            selectTagFromAllTagsWithLabel(getState(), label)?.get('key');
        return new Promise((resolve, reject) => {
            if (!keyOfTagToRemove) {
                // Without a key the request would go to /tags/undefined and 404.
                reject(new Error(`No key found for tag with label ${label}`));
                return;
            }

            TicketsResource.removeTag(
                {
                    onResult(json) {
                        dispatch(
                            setTagRemovedFromTicket(
                                ticketKey,
                                keyOfTagToRemove,
                            ),
                        );
                        resolve(json);
                    },
                    onClientError: (error) => {
                        dispatch(onTicketWriteRefused(ticketKey, error));
                        reject(error);
                    },
                    onError: (error) => {
                        networkErrorDispatcher(dispatch);
                        reject(error);
                    },
                },
                ticketKey,
                keyOfTagToRemove,
            );
        });
    };

export const deleteTagsCompletely = (tagKeys) => (dispatch) => {
    return new Promise((resolve /*, reject*/) => {
        dispatch(setDeleteTagsCompletely(tagKeys));
        resolve();
        /*
        TicketsResource.deleteTag(
            {
                onResult(json) {
                    dispatch(setDeleteTagCompletely(tagKey));
                    resolve(json);
                },
                onClientError: (error) => {
                    reject(error);
                },
                onError: (error) => {
                    networkErrorDispatcher(dispatch);
                    reject(error);
                },
            },
            tagKey
        );
        */
    });
};

export const renameTag = (tagKey, newName) => (dispatch) => {
    return new Promise((resolve /*, reject*/) => {
        dispatch(setRenameTag(tagKey, newName));
        resolve();
        /*
        TicketsResource.renameTag(
            {
                onResult(json) {
                    dispatch(setRenameTag(tagKey, newName));
                    resolve(json);
                },
                onClientError: (error) => {
                    reject(error);
                },
                onError: (error) => {
                    networkErrorDispatcher(dispatch);
                    reject(error);
                },
            },
            tagKey
        );
        */
    });
};

export const sendWhatsAppTemplateMessage =
    (ticketKey, templateName, language) => (dispatch) => {
        const messageObject = JSON.stringify({
            template_name: templateName,
            language: language,
        });
        return new Promise((resolve, reject) => {
            TicketsResource.sendWhatsAppTemplateMessage(
                {
                    onResult(json) {
                        resolve(json);
                        // WHATSAPP TO DO:
                        // Remove once we have a Top event for this
                        dispatch(addTicketEvent(json, ticketKey));
                    },
                    onClientError: (error) => {
                        dispatch(onTicketWriteRefused(ticketKey, error));
                        reject(error);
                    },
                    onError: (error) => {
                        networkErrorDispatcher(dispatch);
                        reject(error);
                    },
                },
                ticketKey,
                messageObject,
            );
        });
    };

export const generateSummary =
    (ticketKey, context, type = 'SHORT') =>
    (dispatch) => {
        const requestDTO = { summaryType: type, summaryContext: context };
        return new Promise((resolve, reject) => {
            TicketsResource.generateAISummary(
                {
                    onResult(json) {
                        resolve(json);
                    },
                    onClientError: (error) => {
                        reject(error);
                    },
                    onError: (error) => {
                        networkErrorDispatcher(dispatch);
                        reject(error);
                    },
                },
                ticketKey,
                requestDTO,
            );
        });
    };

export const fetchWhatsAppTemplates = (ticketKey) => (dispatch) => {
    return new Promise((resolve, reject) => {
        TicketsResource.getWhatsAppTemplates(
            {
                onResult(payload) {
                    resolve(payload);
                },
                // Reading the templates is behind the write gate, because they
                // only exist to be sent.
                onClientError: (error) => {
                    dispatch(onTicketWriteRefused(ticketKey, error));
                    reject(error);
                },
                onError: (error) => {
                    networkErrorDispatcher(dispatch);
                    reject(error);
                },
            },
            ticketKey,
        );
    });
};

const getIconForTicketChannel = (channel) => {
    if (!channel) return null;
    if (channel === 'whatsapp') return icon_whatsapp;
    if (channel === 'email') return icon_email;
    return null;
};

export const spawnNotificationNewTicket =
    (ticketKey, message, channel, notificationKey) => (dispatch) => {
        const title = i18next.t('tickets.conversation.new');
        dispatch(
            spawnTicketNotification(
                ticketKey,
                title,
                message,
                getIconForTicketChannel(channel),
                notificationKey,
            ),
        );
    };

const setAwaitingActivity = (ticketKey, value) => (dispatch) => {
    dispatch({
        type: Actions.SET_TICKET_AWAITING_ACTIVITY,
        ticketKey,
        value,
    });
};

// A ticket-scoped Top push carries ticketLastUpdate — the ticket's new
// LASTUPDATED, the field ticket lists sort by — whenever the event moved it.
// Handlers apply it when present; WHICH events move it is owned by the
// backend. The reducer only ever moves the value forward (max-guard), so
// out-of-order pushes are safe.
const setTicketLastUpdate = (ticketKey, value) => (dispatch) => {
    dispatch({
        type: Actions.SET_TICKET_LAST_UPDATED,
        ticketKey,
        value,
    });
};

// A pushed ticket update is worth fetching only when we don't already have the
// ticket and the tickets list is mounted to display it.
const qualifiesForTicketFetch = (state, ticketKey) =>
    !selectTicketIsFetched(state, ticketKey) &&
    !!selectTicketsListIsMounted(state);

// Applies a server-pushed lastUpdate from a Top event: if the ticket qualifies
// for a fetch (not loaded + list mounted), fetch it — the fetched ticket carries
// the current lastUpdate — otherwise set it in place (a no-op when we don't have
// the ticket). Handlers that fetch on their own relevance rules
// (assignee/state/members/awaiting) and the created event don't use this.
const applyPushedTicketLastUpdate =
    (ticketKey, lastUpdate) => (dispatch, getState) => {
        if (qualifiesForTicketFetch(getState(), ticketKey)) {
            dispatch(fetchTicket(ticketKey));
        } else {
            dispatch(setTicketLastUpdate(ticketKey, lastUpdate));
        }
    };

const setTicketSubject = (ticketKey, value) => (dispatch) => {
    dispatch({
        type: Actions.SET_TICKET_SUBJECT,
        ticketKey,
        value,
    });
};

const setTicketVisibility = (ticketKey, value) => (dispatch) => {
    dispatch({
        type: Actions.SET_TICKET_VISIBILITY,
        ticketKey,
        value,
    });
};

export const createTicketForCallRecord =
    (callRecordKey, queueKey) => async (dispatch, getState) => {
        const state = getState();
        const ownExtensionKey = selectOwnExtensionKey(state);
        const queueMembers = selectQueueMembers(state, queueKey);
        const memberKeys = queueMembers
            ? queueMembers.map((m) => m.get('extensionKey')).toArray()
            : [];

        const data = await dispatch(
            createTicketForQueueCall(
                callRecordKey,
                memberKeys,
                ownExtensionKey,
            ),
        );

        await dispatch(fetchTicket(data.key));
    };

export const forwardTicketToQueue = (ticketKey, queueKey) => (dispatch) => {
    return new Promise((resolve, reject) => {
        TicketsResource.forwardTicket(
            {
                onResult(json) {
                    dispatch(setTicket(ticketKey, json));
                    resolve(json);
                },
                onClientError: (error) => {
                    dispatch(onTicketWriteRefused(ticketKey, error));
                    reject(error);
                },
                onError: (error) => {
                    networkErrorDispatcher(dispatch);
                    reject(error);
                },
            },
            ticketKey,
            { queueEntityKey: queueKey },
        );
    });
};
